import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { get } from '../services/api';
import gmeLogo from '../assets/gme.png';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for error from Microsoft OAuth callback
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      console.log('Attempting login with:', { email });
      const result = await login(email, password);
      console.log('Login result:', result);
      
      if (result.success) {
        console.log('Login successful, redirecting...');
        // Navigation is now handled in the login function
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-gray-100 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 sm:p-8 space-y-6 sm:space-y-8 border border-gray-100">
        <div className="flex flex-col items-center">
          <img src={gmeLogo} alt="Gastronomica Logo" className="h-16 sm:h-20 w-auto mb-3 sm:mb-4" />
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 text-center mb-2">Sign in to your account</h2>
          <p className="text-gray-500 text-center text-xs sm:text-sm">DocumentHub</p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-t-md relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm bg-gray-50"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                className="appearance-none rounded-b-md relative block w-full px-3 py-3 pr-10 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm bg-gray-50"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <FiEyeOff className="h-5 w-5" />
                ) : (
                  <FiEye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-md text-white bg-purple-700 hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 shadow-md transition"
            >
              Sign in
            </button>
          </div>
        </form>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Microsoft login button clicked');
                
                try {
                  setError(''); // Clear any previous errors
                  
                  // Try to get authorization URL from backend
                  // First try using the API service (which uses the proxy)
                  console.log('Fetching Microsoft authorization URL from backend...');
                  
                  let data;
                  try {
                    data = await get('/api/auth/microsoft?format=json');
                    
                    // Check if we got HTML instead of JSON (proxy not working)
                    if (typeof data === 'string' && data.includes('<!doctype')) {
                      console.warn('Received HTML instead of JSON - proxy may not be working, trying direct backend URL');
                      throw new Error('Proxy not working');
                    }
                  } catch (proxyError) {
                    // If proxy fails, try direct backend URL (for development or if proxy isn't configured)
                    console.log('Proxy failed, trying direct backend URL...');
                    const backendUrl = process.env.NODE_ENV === 'development' 
                      ? 'http://localhost:3000'
                      : `${window.location.protocol}//${window.location.hostname}:3000`;
                    
                    const directUrl = `${backendUrl}/api/auth/microsoft?format=json`;
                    console.log('Trying direct backend URL:', directUrl);
                    
                    const response = await fetch(directUrl, {
                      method: 'GET',
                      credentials: 'include',
                      headers: {
                        'Accept': 'application/json',
                      },
                    });
                    
                    if (!response.ok) {
                      throw new Error(`HTTP ${response.status}`);
                    }
                    
                    data = await response.json();
                  }
                  
                  console.log('Received data:', data);
                  
                  if (data.success && data.authUrl) {
                    console.log('Received authorization URL, redirecting to Microsoft...');
                    // Redirect to Microsoft using the URL from backend
                    window.location.href = data.authUrl;
                  } else {
                    throw new Error(data.message || 'Invalid response from server');
                  }
                } catch (error) {
                  console.error('Error initiating Microsoft login:', error);
                  const errorMessage = error.response?.data?.message || error.message || 'Failed to initiate Microsoft login';
                  setError(errorMessage);
                }
              }}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width="11" height="11" fill="#F25022"/>
                <rect x="12" y="0" width="11" height="11" fill="#7FBA00"/>
                <rect x="0" y="12" width="11" height="11" fill="#00A4EF"/>
                <rect x="12" y="12" width="11" height="11" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;